import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePolyAuth } from '../../../hooks/PolyAuthContext';

/**
 * Drives "Generate Report" for a selected meeting row. Posts to
 * /v2/reports/meeting and hands the resulting URL to openURL. Surfaces
 * generation state + the last error so the modal can render a spinner +
 * inline Alert.
 */
export function useMeetingReportGen({ selectedLog, openURL, onSuccess }) {
    const { t } = useTranslation('shared');
    const { api_client } = usePolyAuth();
    const [generatingReport, setGeneratingReport] = useState(false);
    const [reportError, setReportError] = useState(null);

    const generateReport = async () => {
        if (!selectedLog || selectedLog.callType !== 'meeting') {
            toast(t('callLogs.cannotGenerateReportInvalid'), { type: 'error' });
            return;
        }

        const { meeting_id, meeting_uuid } = selectedLog;
        if (!meeting_id || !meeting_uuid) {
            toast(t('callLogs.cannotGenerateReportMissing'), { type: 'error' });
            return;
        }

        setGeneratingReport(true);
        setReportError(null);

        try {
            toast(t('callLogs.generatingReport'), { type: 'info' });
            const response = await api_client.post('/v2/reports/meeting', { meeting_id, meeting_uuid });
            openURL(response.data.url);
            toast(t('callLogs.reportOpened'), { type: 'success' });
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error generating report:', error);
            setReportError(error.message || t('callLogs.failedToGenerateReport'));
            toast(t('callLogs.failedToGenerateReport'), { type: 'error' });
        } finally {
            setGeneratingReport(false);
        }
    };

    return { generateReport, generatingReport, reportError };
}
